import * as Sentry from '@sentry/react';
import React from 'react';
import { SENTRY_DSN } from 'infrastructure/config';

// 通知から除外するエラーメッセージ
const EXCLUDED_ERROR_MESSAGES = [
    'addEventListener',
    'メールアドレス、パスワードのほか、接続情報をお確かめください',
    '認証が必要です。',
];

if (SENTRY_DSN && import.meta.env.PROD) {
    Sentry.init({
        dsn: SENTRY_DSN,
        environment: import.meta.env.VITE_ENV ?? import.meta.env.MODE,
        beforeSend(event) {
            console.log('event', event);
            if (
                event.exception?.values?.some((exception) =>
                    EXCLUDED_ERROR_MESSAGES.some((excludedMessage) => exception.value?.includes(excludedMessage)),
                )
            ) {
                return null;
            }
            return event;
        },
    });
} else {
    console.warn('Sentry is not initialized.');
}

export const SentryErrorBoundary = Sentry.withErrorBoundary(
    ({ children }: { children: React.ReactNode }) => React.createElement(React.Fragment, null, children),
    {},
);

export class SentryUtil {
    static send(error: Error | string, metadata?: Record<string, unknown>): void {
        if (!SENTRY_DSN) return;

        const errorObj = typeof error === 'string' ? new Error(error) : error;

        Sentry.withScope((scope) => {
            if (metadata) {
                Object.keys(metadata).forEach((key) => {
                    scope.setContext(key, metadata[key] as Record<string, unknown>);
                });
            }
            Sentry.captureException(errorObj);
        });
    }

    static sendMessage(
        message: string,
        level: 'debug' | 'info' | 'warning' | 'error' | 'fatal' = 'info',
        metadata?: Record<string, unknown>,
    ): void {
        if (!SENTRY_DSN) return;

        Sentry.withScope((scope) => {
            scope.setLevel(level);
            if (metadata) {
                Object.keys(metadata).forEach((key) => {
                    scope.setContext(key, metadata[key] as Record<string, unknown>);
                });
            }
            Sentry.captureMessage(message, level);
        });
    }

    static setUser(user: { id?: string; email?: string; username?: string; [key: string]: unknown }): void {
        Sentry.setUser(user);
    }

    static setTags(tags: Record<string, string>): void {
        Sentry.setTags(tags);
    }

    static setContext(key: string, context: Record<string, unknown>): void {
        Sentry.setContext(key, context);
    }
}
